import React from 'react';
import { Einwohnermeldeamt } from '../../types/einwohnermeldeamt';
import { formatNumber, getCleanLocationName } from '../../utils/einwohnermeldeamtHelpers';

interface LocationInfoGridProps {
  location: Einwohnermeldeamt;
}

const LocationInfoGrid: React.FC<LocationInfoGridProps> = ({ location }) => {
  // Helper function to check if a value is available
  const isValueAvailable = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'number' && value === 0) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  };

  // Helper function to generate Google Maps URL
  const getGoogleMapsUrl = (address: string): string => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  // Build info sections dynamically based on available data
  const buildInfoSections = () => {
    const sections = [];

    // Contact Information Section
    const contactItems = [
      isValueAvailable(location.strasse) && isValueAvailable(location.ort) && 
        { 
          label: 'Anschrift', 
          value: `${location.strasse}, ${location.ort}`,
          mapsLink: getGoogleMapsUrl(`${location.strasse}, ${location.postleitzahl || ''} ${location.ort}`)
        },
      isValueAvailable(location.postleitzahl) && 
        { label: 'Postleitzahl', value: location.postleitzahl },
      isValueAvailable(location.gemeindeName) && 
        { label: 'Gemeinde', value: getCleanLocationName(location.gemeindeName) }
    ].filter(Boolean);

    if (contactItems.length > 0) {
      sections.push({
        title: 'Kontaktinformationen',
        icon: (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
        ),
        items: contactItems
      });
    }

    // Statistical Data Section
    const statisticalItems = [
      isValueAvailable(location.einwohner) && 
        { label: 'Einwohner', value: formatNumber(location.einwohner) },
      isValueAvailable(location.flaecheKm2) && 
        { label: 'Fläche', value: `${formatNumber(location.flaecheKm2)} km²` },
      isValueAvailable(location.amtlGemeindeschluessel) && 
        { label: 'Gemeindeschlüssel', value: location.amtlGemeindeschluessel }
    ].filter(Boolean);

    if (statisticalItems.length > 0) {
      sections.push({
        title: 'Statistische Daten',
        icon: (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
          </svg>
        ),
        items: statisticalItems
      });
    }

    // Regional Assignment Section
    const regionalItems = [
      isValueAvailable(location.bundesland) && 
        { label: 'Bundesland', value: location.bundesland },
      isValueAvailable(location.region) && 
        { label: 'Region', value: location.region },
      isValueAvailable(location.kreis) && 
        { label: 'Kreis', value: location.kreis },
      isValueAvailable(location.gemeindetyp) && 
        { label: 'Gemeindetyp', value: location.gemeindetyp }
    ].filter(Boolean);

    if (regionalItems.length > 0) {
      sections.push({
        title: 'Regionale Zuordnung',
        icon: (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z" clipRule="evenodd" />
          </svg>
        ),
        items: regionalItems
      });
    }

    return sections;
  };

  const infoSections = buildInfoSections();

  // If no sections have data, don't render anything
  if (infoSections.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {infoSections.map((section, index) => (
        <div
          key={index}
          className="bg-white rounded-xl p-6 shadow-md border border-gray-200"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg" style={{ backgroundColor: '#e8f4f8' }}>
              <div style={{ color: '#023047' }}>{section.icon}</div>
            </div>
            <h3 className="text-lg font-bold" style={{ color: '#023047' }}>
              {section.title}
            </h3>
          </div>
          
          <dl className="space-y-3">
            {section.items.map((item: any, itemIndex: number) => (
              <div key={itemIndex}>
                <dt className="text-sm text-gray-600 mb-1">{item.label}</dt>
                <dd className="text-base font-medium text-gray-900">
                  {item.mapsLink ? (
                    <a 
                      href={item.mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 hover:text-blue-600 transition-colors group"
                      title="In Google Maps öffnen"
                    >
                      <span>{item.value}</span>
                      <svg 
                        className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        <path 
                          fillRule="evenodd" 
                          d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" 
                          clipRule="evenodd" 
                        />
                      </svg>
                      <svg 
                        className="w-3 h-3 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                      </svg>
                    </a>
                  ) : (
                    item.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
};

export default LocationInfoGrid;

